<script setup lang="ts">
import { generateBrandColorFromLogo } from '~/utils/colorUtils'
import { generateCompanyMeta } from '~/utils/meta/generateCompanyMeta'
import { companyLinks } from '~/utils/menuLinks'
import { getGeoDefaultLocationCode } from '~/utils/getDefaultLocationCode'
import {
  fetchCompanyData,
  fetchOverviewData,
  fetchReviewsData,
  fetchDressCodeData,
  fetchDrugTestData,
  fetchHiringProcessData,
  fetchJobsData,
  fetchSalariesData,
  updateCompanyBrandColor
} from '~/utils/companyDataFetcher'
import { hasInsufficientSalaryData } from '~/utils/salaryDataValidator'
import RelatedCompanies from '~/components/company/RelatedCompanies.vue'
import GeneralDisclaimer from '~/components/common/GeneralDisclaimer.vue'
import { formatDate, formatLocationDisplay } from '~/utils/formatters'
import ShareOnSocialMedia from '~/components/ShareOnSocialMedia.vue'
interface Location {
  city: string | null
  state: string | null
  zipcode?: string | null
}
definePageMeta({
  layout: 'marketing',
  middleware: [
    'company-validation',
    async (route) => {
      const segments = route.params.permalink as string[]
      if (segments[1] === 'jobs' && segments.length > 2) {
        const companySlug = segments[0]
        const locationParam = segments[2]
        const toTitleCase = (str: string) => {
          return str
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        }
        let state = null
        let city = null
        const hasLocationParam = true
        const cityStateMatch = locationParam.match(/^([^-]+(?:-[^-]+)*)-([A-Za-z]{2})$/)
        if (cityStateMatch) {
          city = toTitleCase(cityStateMatch[1].replace(/-/g, ' ').trim())
          state = cityStateMatch[2].toUpperCase()
        } else {
          const stateMatch = stateOptions.find(
            s => s.label.toLowerCase().replace(/\s+/g, '-') === locationParam.toLowerCase()
          )
          if (stateMatch) {
            state = stateMatch.code
          }
        }
        if (!state && !city) {
          return navigateTo(`/company/${companySlug}/jobs`, { redirectCode: 301 })
        }
        try {
          const statesCities = await $fetch<Record<string, Array<string | { name: string }>>>(`/api/companies/${companySlug}/jobs/locations`, {
            method: 'POST'
          })
          if (state) {
            const stateFullName = stateOptions.find(s => s.code === state)?.label
            const isValidState = stateFullName ? Object.keys(statesCities).includes(stateFullName) : false
            if (!isValidState) {
              return navigateTo(`/company/${companySlug}/jobs`, { redirectCode: 301 })
            }
            if (city) {
              const citiesInState = statesCities[stateFullName] || []
              const isValidCity = citiesInState.some((c: any) =>
                (typeof c === 'string' ? c : c.name)?.toLowerCase() === city.toLowerCase()
              )
              if (!isValidCity) {
                return navigateTo(`/company/${companySlug}/jobs`, { redirectCode: 301 })
              }
            }
          } else if (city) {
            return navigateTo(`/company/${companySlug}/jobs`, { redirectCode: 301 })
          }
        } catch (error) {
          console.error('Error validating jobs location:', error)
        }
      }
      if (segments[1] === 'salaries' && segments.length > 2) {
        return true
      }
    }
  ]
})
const route = useRoute()
const jobstatus = computed(() => (route.query.jobstatus as string) || '')
const pageQuery = computed(() => parseInt(route.query.page as string) || 1)
const userZipCode = useState('userZipCode', () => getGeoDefaultLocationCode())
const toast = useToast()
const companySlug = computed(() => {
  const params = route.params.permalink as string[]
  return params?.[0] || ''
})
const section = computed<
  'overview' | 'reviews' | 'salaries' | 'jobs' | 'dress-code' | 'drug-test-policy' | 'hiring-process'
>(() => {
  const params = route.params.permalink as string[]
  const sec = params[1]
  return sec === 'overview'
    || sec === 'reviews'
    || sec === 'salaries'
    || sec === 'jobs'
    || sec === 'dress-code'
    || sec === 'drug-test-policy'
    || sec === 'hiring-process'
    ? sec
    : 'overview'
})
const parseLocation = (locationString: string | null): Location => {
  if (!locationString) return { city: null, state: null, zipcode: null }
  const toTitleCase = (str: string) => {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
  const cityStateMatch = locationString.match(
    /^([^-]+(?:-[^-]+)*)-([A-Za-z]{2})$/
  )
  if (cityStateMatch) {
    const cityPart = toTitleCase(cityStateMatch[1].replace(/-/g, ' ').trim())
    const statePart = cityStateMatch[2].toUpperCase()
    return {
      city: cityPart,
      state: statePart,
      zipcode: null
    }
  }
  if (/^[A-Za-z]{2}$/.test(locationString)) {
    return { city: null, state: null, zipcode: null }
  }
  const stateMatch = stateOptions.find(
    state =>
      state.label.toLowerCase().replace(/\s+/g, '-')
      === locationString.toLowerCase()
  )
  if (stateMatch) {
    return {
      city: null,
      state: stateMatch.code,
      zipcode: null
    }
  }
  return { city: null, state: null, zipcode: null }
}
const locationParam = computed(() => {
  const params = route.params.permalink as string[]
  return params[2] || null
})
const roleParam = computed(() => {
  const params = route.params.permalink as string[]
  return params[3] || null
})
const keywordParam = computed(() => {
  const params = route.params.permalink as string[]
  return params[3] || null
})
const location = computed<Location>(() => {
  return parseLocation(locationParam.value)
})
const displayLocation = computed(() => {
  if (location.value.zipcode) {
    return location.value.zipcode
  }
  if (location.value.city && location.value.state) {
    return `${location.value.city}, ${location.value.state}`
  }
  return location.value.state || userZipCode.value
})
const asyncDataKey = computed(() => {
  return `company-${companySlug.value}-${section.value}-${locationParam.value || ''
    }-${roleParam.value || ''}`
})
const {
  data: pageData,
  pending,
  refresh: refreshData
} = await useAsyncData(
  asyncDataKey.value,
  async () => {
    if (!companySlug.value || companySlug.value.trim() === '') {
      return null
    }
    try {
      const locationData = location.value
      const roleData = roleParam.value
        ? decodeURIComponent(roleParam.value).replace(/-/g, ' ')
        : ''
      let company
      company = await fetchCompanyData(companySlug.value)
      if (!company?.success || !('data' in company) || !company.data) {
        return null
      }
      let sectionData: any = {}
      const validateData = (data: any) => {
        if (roleData && Array.isArray(data.rolesData)) {
          data.isValidRole = data.rolesData.some(
            (role: any) =>
              role.popular_title?.toLowerCase() === roleData.toLowerCase()
          )
        }
        if (locationData.city && Array.isArray(data.citiesByState)) {
          data.isValidCity = data.citiesByState.some(
            (city: any) =>
              city.name?.toLowerCase() === locationData.city.toLowerCase()
          )
        }
      }
      if (company.success && 'data' in company) {
        switch (section.value) {
          case 'overview':
            sectionData = await fetchOverviewData(
              companySlug.value,
              userZipCode.value,
              company.data?.jobs_company_name || company.data?.brand_name || ''
            )
            break
          case 'salaries':
            // Note: preloadStateData removed - fetchSalariesData below handles state data loading
            // and middleware already caches the result for reuse
            const salariesJobsData = await $fetch(`/api/jobs/indexation/company`, {
              method: 'POST',
              body: {
                company_permalink: companySlug.value,
                company_name: company.data?.jobs_company_name || company.data?.brand_name || '',
                limit: '10'
              }
            })
            sectionData = await fetchSalariesData(companySlug.value, {
              role: roleData,
              state: locationData.state,
              city: locationData.city
            })
            const processedSalariesJobs = salariesJobsData?.success && (salariesJobsData as any).jobs
              ? (salariesJobsData as any).jobs.map((job: any) => ({
                ...job,
                logo: company.data?.logo_url || job.logo
              }))
              : []
            sectionData.jobsData = salariesJobsData?.success
              ? {
                jobs: processedSalariesJobs,
                total: (salariesJobsData as any).total || 0,
                available: (salariesJobsData as any).available || 0
              }
              : { jobs: [], total: 0, available: 0 }
            validateData(sectionData)
            break
          case 'reviews':
            const reviewsJobsData = await $fetch(`/api/jobs/indexation/company`, {
              method: 'POST',
              body: {
                company_permalink: companySlug.value,
                company_name: company.data?.jobs_company_name || company.data?.brand_name || '',
                limit: '10'
              }
            })
            sectionData = await fetchReviewsData(companySlug.value, {
              role: roleData,
              state: locationData.state,
              city: locationData.city
            })
            const processedReviewsJobs = reviewsJobsData?.success && (reviewsJobsData as any).jobs
              ? (reviewsJobsData as any).jobs.map((job: any) => ({
                ...job,
                logo: company.data?.logo_url || job.logo
              }))
              : []
            sectionData.jobsData = reviewsJobsData?.success
              ? {
                jobs: processedReviewsJobs,
                total: (reviewsJobsData as any).total || 0,
                available: (reviewsJobsData as any).available || 0
              }
              : { jobs: [], total: 0, available: 0 }
            validateData(sectionData)
            break
          case 'dress-code': {
            const dressCodeJobsData = await $fetch(`/api/jobs/indexation/company`, {
              method: 'POST',
              body: {
                company_permalink: companySlug.value,
                company_name: company.data?.jobs_company_name || company.data?.brand_name || '',
                limit: '10'
              }
            })
            const dressCodeData = await fetchDressCodeData(companySlug.value)
            const processedDressCodeJobs = dressCodeJobsData?.success && (dressCodeJobsData as any).jobs
              ? (dressCodeJobsData as any).jobs.map((job: any) => ({
                ...job,
                logo: company.data?.logo_url || job.logo
              }))
              : []
            sectionData = {
              dressCodeData,
              jobsData: dressCodeJobsData?.success
                ? {
                  jobs: processedDressCodeJobs,
                  total: (dressCodeJobsData as any).total || 0,
                  available: (dressCodeJobsData as any).available || 0
                }
                : { jobs: [], total: 0, available: 0 }
            }
            break
          }
          case 'drug-test-policy': {
            const drugTestJobsData = await $fetch(`/api/jobs/indexation/company`, {
              method: 'POST',
              body: {
                company_permalink: companySlug.value,
                company_name: company.data?.jobs_company_name || company.data?.brand_name || '',
                limit: '10'
              }
            })
            const drugTestData = await fetchDrugTestData(companySlug.value)
            const processedDrugTestJobs = drugTestJobsData?.success && (drugTestJobsData as any).jobs
              ? (drugTestJobsData as any).jobs.map((job: any) => ({
                ...job,
                logo: company.data?.logo_url || job.logo
              }))
              : []
            sectionData = {
              drugTestData,
              jobsData: drugTestJobsData?.success
                ? {
                  jobs: processedDrugTestJobs,
                  total: (drugTestJobsData as any).total || 0,
                  available: (drugTestJobsData as any).available || 0
                }
                : { jobs: [], total: 0, available: 0 }
            }
            break
          }
          case 'hiring-process': {
            const hiringProcessJobsData = await $fetch(`/api/jobs/indexation/company`, {
              method: 'POST',
              body: {
                company_permalink: companySlug.value,
                company_name: company.data?.jobs_company_name || company.data?.brand_name || '',
                limit: '10'
              }
            })
            const hiringProcessData = await fetchHiringProcessData(companySlug.value)
            const processedJobs = hiringProcessJobsData?.success && (hiringProcessJobsData as any).jobs
              ? (hiringProcessJobsData as any).jobs.map((job: any) => ({
                ...job,
                logo: company.data?.logo_url || job.logo
              }))
              : []
            sectionData = {
              hiringProcessData,
              jobsData: hiringProcessJobsData?.success
                ? {
                  jobs: processedJobs,
                  total: (hiringProcessJobsData as any).total || 0,
                  available: (hiringProcessJobsData as any).available || 0
                }
                : { jobs: [], total: 0, available: 0 }
            }
            break
          }
          case 'jobs':
            const userAgent = import.meta.client ? navigator.userAgent : 'SSR'
            const browserLanguage = import.meta.client
              ? navigator.language.split('-')[0]
              : 'en'
            let statesCities: Record<string, Array<string | { name: string }>> = {}
            try {
              statesCities = await $fetch<Record<string, Array<string | { name: string }>>>(`/api/companies/${companySlug.value}/jobs/locations`, {
                method: 'POST'
              })
            } catch (err) {
            }
            const requestedPage = parseInt(route.query.page as string) || 1
            if (requestedPage < 1) {
              return null
            }
            const jobsData = await fetchJobsData(
              companySlug.value,
              displayLocation.value,
              {
                volcid: (route.query.volcid as string) || '',
                type: (route.query.type as string) || '',
                origin: (route.query.origin as string) || '',
                company_name: company.data?.jobs_company_name || company.data?.brand_name || '',
                state: locationData.state || '',
                city: locationData.city || '',
                page: requestedPage
              }
            )
            // Validate page exists - return null if page is beyond available pages
            const totalJobs = jobsData?.total || jobsData?.available || 0
            const perPage = 20
            const totalPages = totalJobs > 0 ? Math.ceil(totalJobs / perPage) : 1
            if (requestedPage > 1 && requestedPage > totalPages) {
              return null
            }
            sectionData = {
              ...(jobsData || {
                jobs: [],
                total: 0,
                available: 0,
                success: false
              }),
              statesCities
            }
            break
        }
      }
      return {
        company,
        initialFilters: {
          state: locationData.state,
          city: locationData.city,
          role: roleData
        },
        sectionData
      }
    } catch (err: any) {
      console.error('Error in company page:', err)
      return null
    }
  },
  { server: true }
)

// Set error state for template and HTTP status code
const hasPageError = computed(() => !pageData.value || !pageData.value.company)

// Set HTTP 404 status code for SSR when page has error
if (hasPageError.value) {
  const event = useRequestEvent()
  if (event) {
    setResponseStatus(event, 404)
  }
}
const company = computed(() => {
  const comp = pageData.value?.company
  return comp?.success && 'data' in comp ? comp.data : null
})
provide('companyData', pageData)
provide('company', company)
provide(
  'companyLogoUrl',
  computed(() => {
    if (company.value?.logo_url) {
      return company.value.logo_url
    }
    if (
      pageData.value?.company
      && 'data' in pageData.value.company
      && pageData.value.company.data?.logo_url
    ) {
      return pageData.value.company.data.logo_url
    }
    return ''
  })
)
const brandColor = ref('#5F0CE2')
const { session } = useUserSession()
provide('session', session)
const userReview = ref(null)
const fetchUserReview = async () => {
  if (!session.value?.user?.id) return null
  try {
    const response = await $fetch<{ success: boolean, review: any }>(
      `/api/companies/${companySlug.value}/reviews/user/${session.value.user.id}`
    )
    if (response.success && response.review) {
      userReview.value = {
        ...response.review,
        company: {
          ...(response.review.company || {}),
          permalink: companySlug.value,
          brand_name: company.value?.brand_name || '',
          logo_url: company.value?.logo_url || ''
        }
      }
    } else {
      userReview.value = null
    }
  } catch (error) {
    console.error('Error fetching user review:', error)
    userReview.value = null
  }
}
onMounted(fetchUserReview)
onMounted(() => {
  if (import.meta.client) {
    const savedZip = localStorage.getItem('user_zip_code')
    if (savedZip) {
      userZipCode.value = savedZip
    }
    if (jobstatus.value === 'expired') {
      toast.add({
        description: 'This job posting has expired and is no longer available.',
        color: 'red',
        icon: 'i-heroicons-x-circle'
      })
    }
  }
})
watch(
  () => company.value,
  async (newCompany) => {
    if (!newCompany) {
      brandColor.value = '#5F0CE2'
      return
    }
    if (newCompany.brand_color) {
      brandColor.value = newCompany.brand_color
      return
    }
    if (newCompany.logo_url) {
      try {
        const generatedColor = await generateBrandColorFromLogo(
          newCompany.logo_url
        )
        brandColor.value = generatedColor || '#5F0CE2'
        if (generatedColor) {
          const response = await updateCompanyBrandColor(
            companySlug.value,
            generatedColor
          )
          if (!response.success) {
            console.error('Failed to update brand color:', response.error)
          }
        }
      } catch (error) {
        console.error('Error generating brand color:', error)
        brandColor.value = '#5F0CE2'
      }
    }
  },
  { immediate: true }
)
const hasDressCode = computed(() => {
  return Boolean(
    (company.value as { dress_code?: unknown })?.dress_code
    && typeof (company.value as { dress_code?: unknown }).dress_code === 'object'
  )
})
const hasDrugTest = computed(() => {
  return Boolean(
    (company.value as { drug_test?: unknown })?.drug_test
    && typeof (company.value as { drug_test?: unknown }).drug_test === 'object'
  )
})
const tabs = computed(() => {
  const baseTabs = companyLinks(companySlug.value).map(link => ({
    name: link.name,
    to: {
      path: link.to.replace('%s', companySlug.value),
      hash: undefined
    },
    value: link.value
  }))
  let filteredTabs = baseTabs
  if (!hasDressCode.value) {
    filteredTabs = filteredTabs.filter(tab => tab.value !== 'dress-code')
  }
  return filteredTabs
})
const currentFilters = reactive({
  role: roleParam.value
    ? decodeURIComponent(roleParam.value).replace(/-/g, ' ')
    : '',
  roleName: '',
  role_id: '',
  state: location.value.state || '',
  city: location.value.city || '',
  originalCityFormat: ''
})
watch(
  [roleParam, location],
  async ([newRole, newLocation]) => {
    if (section.value === 'salaries') {
      const decodedRole = newRole
        ? decodeURIComponent(newRole).replace(/-/g, ' ')
        : ''
      currentFilters.role = decodedRole
      currentFilters.state = newLocation.state || ''
      currentFilters.city = newLocation.city || ''
    } else if (section.value === 'jobs') {
      currentFilters.state = newLocation.state || ''
      currentFilters.city = newLocation.city || ''
    }
  },
  { immediate: true }
)
const selectedRole = computed(() => currentFilters.role || '')
const selectedState = computed(() => currentFilters.state || '')
const selectedCity = computed(() => {
  return currentFilters.city || ''
})
const handleSalaryFiltersUpdate = (filters) => {
  currentFilters.role = filters.role.title
  currentFilters.state = filters.state
  currentFilters.city = filters.city
}
const handleReviewFiltersUpdate = (filters) => {
  currentFilters.roleName = filters.roleName
  currentFilters.role = filters.roleName
  currentFilters.role_id = filters.role_id
  currentFilters.state = filters.state
  currentFilters.city = filters.city
  currentFilters.originalCityFormat = filters.originalCityFormat
}
const roleInfo = computed(() => {
  if (!roleParam.value || !pageData.value?.sectionData)
    return { description: null, soc_code: null }
  const rolesData = (pageData.value.sectionData as any).rolesData
  if (!rolesData) return { description: null, soc_code: null }
  const roleData = Array.isArray(rolesData) ? rolesData : rolesData?.data || []
  const decodedRoleParam = decodeURIComponent(roleParam.value)
    .replace(/-/g, ' ')
    .toLowerCase()
  const matchingRole = roleData.find(
    role =>
      (role.popular_title || role.title || '').toLowerCase()
      === decodedRoleParam
  )
  return {
    description: matchingRole?.description || null,
    soc_code: matchingRole?.soc_code || null,
    title: matchingRole?.title || null
  }
})
const companyNameRef = computed(() => company.value?.brand_name || '')
const stateFullNameRef = computed(() => location.value?.state || '')
const locationDisplayRef = computed(() =>
  formatLocationDisplay(location.value)
)
const salaryFilters = computed(() => ({
  role: roleParam.value
    ? decodeURIComponent(roleParam.value).replace(/-/g, ' ')
    : '',
  city: location.value.city || '',
  state: location.value.state || ''
}))
const { titleText: salaryTitleQuestion } = useTitleGenerator(
  salaryFilters.value,
  companyNameRef,
  stateFullNameRef,
  locationDisplayRef
)
const salaryPageTitle = computed(() => {
  if (section.value !== 'salaries') return null
  if (salaryTitleQuestion.value) {
    return salaryTitleQuestion.value
  }
  const companyName = company.value?.brand_name || ''
  const { year, month } = formatDate()
  const roleName = roleParam.value
    ? decodeURIComponent(roleParam.value).replace(/-/g, ' ')
    : ''
  const formattedLocation = formatLocationDisplay(location.value)
  if (roleName && formattedLocation) {
    return `${companyName} ${roleName} Salaries in ${formattedLocation} (${year})`
  } else if (roleName) {
    return `${companyName} ${roleName} Salaries in ${year}`
  } else if (formattedLocation) {
    return `${companyName} Salaries in ${formattedLocation} (${year})`
  } else {
    return `${companyName} Salaries in ${year}`
  }
})
const shareTitle = computed(() => {
  const companyName = company.value?.brand_name || company.value?.company_name || ''
  const sectionTitles = {
    'overview': `Share ${companyName} Company Pages`,
    'reviews': `Share ${companyName} Company Reviews`,
    'salaries': `Share ${companyName} Company Salaries`,
    'jobs': `Share ${companyName} Company Jobs`,
    'dress-code': `Share ${companyName} Company Dress Code`
  }
  return sectionTitles[section.value] || `Share ${companyName}`
})
const shouldNoIndexSalaryPage = computed(() => {
  if (section.value !== 'salaries') return false
  return hasInsufficientSalaryData({
    sectionData: pageData.value?.sectionData,
    roleParam: roleParam.value,
    location: location.value
  })
})
const meta = generateCompanyMeta({
  companyName: company.value?.brand_name,
  companySlug: companySlug.value,
  section: section.value,
  location: location.value,
  roleParam: roleParam.value,
  keywordParam: keywordParam.value,
  companyLogo: company.value?.logo_url,
  noIndex: shouldNoIndexSalaryPage.value,
  industry: company.value?.industry || null,
  roleInfo: roleInfo.value || null,
  salaryDetail:
    section.value === 'salaries'
      && (pageData.value?.sectionData as any)?.salaryDetail
      ? (pageData.value.sectionData as any).salaryDetail
      : null,
  salaryPageTitle: section.value === 'salaries' ? salaryPageTitle.value : null,
  dressCodeData:
    section.value === 'dress-code'
      ? (pageData.value?.sectionData as any)?.dressCodeData
      : null,
  drugTestData:
    section.value === 'drug-test-policy'
      ? (pageData.value?.sectionData as any)?.drugTestData
      : null,
  hiringProcessData:
    section.value === 'hiring-process'
      ? (pageData.value?.sectionData as any)?.hiringProcessData
      : null,
  reviewStats:
    section.value === 'reviews'
      && (pageData.value?.sectionData as any)?.reviewsData?.stats
      ? {
        total: (pageData.value.sectionData as any).reviewsData.stats.total,
        overallRating: (pageData.value.sectionData as any).reviewsData.stats.overallRating
      }
      : null,
  page: section.value === 'jobs' ? pageQuery.value : undefined,
  reviews:
    section.value === 'reviews'
      && (pageData.value?.sectionData as any)?.reviewsData?.reviews
      ? (pageData.value.sectionData as any).reviewsData.reviews
      : [],
  companyDescription: company.value?.description || '',
  companyWebsite: company.value?.website || ''
})
</script>
<template>
  <div>
    <!-- Error Page -->
    <ErrorsErrorPage
      v-if="hasPageError"
      :status-code="404"
      title="This page has moved or no longer exists."
      subtitle="The page you're looking for doesn't exist or has been moved."
    />
    <div v-else-if="pending" class="text-center py-12">
      <div class="animate-pulse">
        <div class="h-24 w-24 bg-gray-200 rounded-full mx-auto mb-6" />
        <div class="h-8 w-64 bg-gray-200 rounded mx-auto" />
        <div class="h-4 w-48 bg-gray-200 rounded mx-auto mt-4" />
      </div>
    </div>
    <template v-else-if="pageData?.company">
      <CompanyHeader :company="company" :brand-color="brandColor" :has-review="!!userReview" :section="section"
        :location="location" :role="roleParam" />
      <CompanyHeroBanner :brand-color="brandColor" :brand-name="company.brand_name" :hero-url="company.hero_url"
        :section="section" :selected-role="selectedRole" :selected-state="selectedState"
        :selected-city="selectedCity" />
      <CompanyNavigation :company-slug="companySlug" :current-section="section" :tabs="tabs" />
      <main class="pb-8">
        <Container>
          <CompanyContent v-if="section === 'overview'" :company="company" :user-zip-code="userZipCode"
            :pre-fetched-data="pageData.sectionData" :logo_url="company.logo_url" :brand-color="brandColor" />
          <CompanyReviews v-else-if="section === 'reviews'" :permalink="company.permalink"
            :company_name="company.brand_name || company.company_name" :user-review="userReview" :city="location.city"
            :state="location.state" :initial-role="roleParam ? decodeURIComponent(roleParam).replace(/-/g, ' ') : ''
              " :pre-fetched-data="pageData.sectionData" :logo-url="company.logo_url" :brand-color="brandColor"
            @review-updated="fetchUserReview" @filters-updated="handleReviewFiltersUpdate" />
          <CompanySalaries v-else-if="section === 'salaries'" :permalink="company.permalink"
            :company_name="company.brand_name || company.company_name" :city="location.city" :state="location.state"
            :initial-role="roleParam ? decodeURIComponent(roleParam).replace(/-/g, ' ') : ''
              " :initial-filters="pageData.initialFilters" :pre-fetched-data="pageData.sectionData"
            :logo-url="company.logo_url" :brand-color="brandColor" @filters-updated="handleSalaryFiltersUpdate" />
          <CompanyJobs v-else-if="section === 'jobs'" :permalink="company.permalink" :location="displayLocation"
            :city="location.city" :state="location.state" :zipcode="location.zipcode" :keyword="keywordParam"
            :company_name="company.jobs_company_name || company.brand_name || company.company_name"
            :pre-fetched-data="pageData.sectionData" :states-cities="pageData.sectionData?.statesCities || {}"
            :logo_url="company.logo_url" :initial-page="pageQuery" />
          <CompanyDressCode v-else-if="section === 'dress-code'" :permalink="company.permalink"
            :brand_color="brandColor"
            :dress_code="(pageData.sectionData as any)?.dressCodeData || (company as any).dress_code"
            :company_name="company.brand_name || company.company_name" :pre-fetched-data="pageData.sectionData"
            :logo-url="company.logo_url" />
          <CompanyDrugTest v-else-if="section === 'drug-test-policy'" :permalink="company.permalink"
            :brand_color="brandColor"
            :drug_test="(pageData.sectionData as any)?.drugTestData || (company as any).drug_test"
            :company_name="company.brand_name || company.company_name" :pre-fetched-data="pageData.sectionData"
            :logo-url="company.logo_url" />
          <CompanyHiringProcess v-else-if="section === 'hiring-process'" :permalink="company.permalink"
            :brand-color="brandColor" :hiring-process="(pageData.sectionData as any)?.hiringProcessData"
            :company-name="company.jobs_company_name || company.brand_name || company.company_name"
            :jobs-data="(pageData.sectionData as any)?.jobsData" :logo-url="company.logo_url"
            :pre-fetched-data="pageData.sectionData" />
          <RelatedCompanies :companies="company.related_companies"
            :company-name="company.brand_name || company.company_name" :section="section" />
          <ShareOnSocialMedia class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" :text="`Check out ! ${meta.description}`"
            :title="shareTitle" />
          <GeneralDisclaimer v-if="section === 'dress-code'"
            disclaimer="Dress code information is compiled from publicly available sources and is intended for general guidance. Policies may vary by location, job role, or store manager discretion. Are you a business owner or company representative?"
            link-text="Contact us" link-to="/employers/contact" post-text="to suggest updates to this page."
            class="mt-8" />
          <GeneralDisclaimer v-else-if="section === 'drug-test-policy'"
            disclaimer="This information is provided for general guidance only and may vary by employer, location, and changes in law or company policy. Always review your official offer and onboarding paperwork. Source: Data from research on career sites and employee policy documentation."
            class="mt-8" />
          <GeneralDisclaimer v-else-if="section === 'salaries'"
            disclaimer="Figures represent approximate ranges and may vary based on experience, location, and other factors. For the most accurate information, please consult the employer directly."
            link-text="Contact us" link-to="/employers/contact" post-text="to suggest updates to this information."
            class="mt-8" />
          <GeneralDisclaimer v-else-if="section === 'reviews'" :disclaimer="`All employee reviews are sourced from Employers.io users, trusted partners, and publicly available information. Opinions expressed in reviews reflect individual experiences and do not represent official statements from ${company.brand_name || company.company_name
            }.`" link-text="Contact us" link-to="/employers/contact" post-text="to suggest updates to this page."
            class="mt-8" />
          <GeneralDisclaimer v-else-if="section === 'jobs'"
            disclaimer="Job listings are aggregated from multiple sources and updated regularly. The availability, requirements, and details may change. Please refer to the employer's official career page for the most accurate and up-to-date information."
            link-text="Contact us" link-to="/employers/contact"
            post-text="to report any issues with these job listings." class="mt-8" />
          <GeneralDisclaimer v-else-if="section === 'hiring-process'"
            disclaimer="Hiring process information is based on publicly available sources including company career pages, applicant reports, and job postings. Actual hiring steps, timelines, and interview questions may vary depending on the position, location, and other factors. Are you a business owner or company representative?"
            link-text="Contact us" link-to="/employers/contact" post-text="to suggest updates to this page."
            class="mt-8" />
          <GeneralDisclaimer v-else
            disclaimer="Company information is compiled from publicly available sources and is intended for general reference. Are you a business owner or company representative?"
            link-text="Contact us" link-to="/employers/contact" post-text="to suggest updates to this page."
            class="mt-8" />
        </Container>
      </main>
    </template>
    <div v-else class="text-center py-12">
      <div class="animate-pulse">
        <div class="h-24 w-24 bg-gray-200 rounded-full mx-auto mb-6" />
        <div class="h-8 w-64 bg-gray-200 rounded mx-auto" />
        <div class="h-4 w-48 bg-gray-200 rounded mx-auto mt-4" />
      </div>
    </div>
  </div>
</template>
<style>
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>